import { createContext, useContext, useState, useEffect } from 'react';
import { STORAGE_KEYS } from '../utils/constants';
import api from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const validateSession = async () => {
      console.log('[AUTH] 🔵 Validation starting...');
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      
      if (!token) {
        console.log('[AUTH] ❌ No token found');
        setLoading(false);
        return;
      }

      console.log('[AUTH] ✓ Token found, validating...');
      
      try {
        // Validate token with backend
        const response = await api.getCurrentUser();
        console.log('[AUTH] ✅ User validated:', response.user?.email);
        setUser(response.user);
        // Update localStorage with fresh user data
        localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(response.user));
      } catch (error) {
        console.error('[AUTH] ⚠️ Validation failed:', error.message);
        console.error('Error details:', {
          message: error.message,
          status: error.status,
          hasToken: !!token
        });
        
        // Only logout on 401 (unauthorized) - keep user logged in for network errors
        if (error.message && error.message.includes('401')) {
          console.log('[AUTH] 🔴 401 detected - logging out');
          logout();
        } else {
          console.log('[AUTH] 🟡 Network error - using cached user');
          // Try to use cached user data
          const cachedUser = localStorage.getItem(STORAGE_KEYS.USER_DATA);
          if (cachedUser) {
            try {
              const parsedUser = JSON.parse(cachedUser);
              setUser(parsedUser);
              console.log('[AUTH] ✅ Cached user loaded:', parsedUser.email);
            } catch (e) {
              console.error('[AUTH] ❌ Cache parse failed:', e);
              logout();
            }
          } else {
            console.log('[AUTH] ❌ No cached user - logging out');
            logout();
          }
        }
      } finally {
        console.log('[AUTH] 🏁 Validation complete - setting loading to false');
        setLoading(false);
      }
    };

    validateSession();
  }, []);

  const login = async (email, password) => {
    const response = await api.login({ email, password });
    
    if (response.token && response.user) {
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, response.token);
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(response.user));
      setUser(response.user);
      return response;
    }
    
    throw new Error('Invalid response from server');
  };

  const register = async (userData) => {
    const response = await api.register(userData);
    
    if (response.token && response.user) {
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, response.token);
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(response.user));
      setUser(response.user);
      return response;
    }
    
    throw new Error('Invalid response from server');
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    setUser(null);
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
